import { Router } from '@angular/router';
import { AuthenticationService } from './services/authentication.service';
import { InitService } from './services/init/init.service';
import { LoadingService } from './services/init/loading-service';
import { UserService } from './services/user/user.service';
import { PermissionCheckerService } from './services/permission/permission-checker.service';
import { ApplicationConfigService } from './services/application-config.service';
import { User } from '@nucleus/user-ui';

const BOOKMARK_KEY = 'bw_redirect';

/** A user with NO bookwise-app role (client/accountant/admin/auditor) but WITH the DevOps
 *  role belongs to the ops console, not this tenant app. Mirrors HomeRedirectGuard's bounce
 *  condition — applied early (right after authorities load) so the client app never fully
 *  boots/renders for such a user, which is what caused the login flicker. */
function shouldBounceToConsole(pc: PermissionCheckerService): boolean {
  return !pc.hasRoleClient() && !pc.hasRoleAccountant() && !pc.hasRoleAdmin()
    && !pc.hasRoleAuditor() && pc.hasRoleDevOps();
}

/** Reveals the body once preferences are hydrated. index.html keeps body
 *  visibility hidden until this fires — so the app never renders with the
 *  wrong theme. Idempotent + double-rAF so the theme attribute change
 *  has actually painted before we unhide. */
function revealApp(): void {
  if (typeof document === 'undefined') return;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    document.body.style.visibility = 'visible';
  }));
}

export function appInitializer(
  authenticationService: AuthenticationService,
  loadingService: LoadingService,
  initService: InitService,
  userService: UserService,
  router: Router,
  permissionChecker: PermissionCheckerService,
  appConfig: ApplicationConfigService
) {
  return () => {
    // Capture the bookmarked route NOW — before Angular's router processes
    // the URL and redirects to /home. The OAuth round-trip strips the hash
    // (redirect_uri is host-only), so we persist it to sessionStorage and
    // recover after the auth callback (?code=...) lands.
    let originalHash = window.location.hash?.replace(/^#\/?/, '') || '';
    const isOAuthCallback = window.location.search.indexOf('?code=') >= 0
                            || window.location.search.indexOf('&code=') >= 0;

    // Pre-OAuth: stash the bookmark.
    if (originalHash && originalHash !== '/' && originalHash !== 'home') {
      sessionStorage.setItem(BOOKMARK_KEY, originalHash);
    }

    // Post-OAuth: hash is empty after callback — pull stash.
    if (!originalHash && isOAuthCallback) {
      originalHash = sessionStorage.getItem(BOOKMARK_KEY) || '';
    }

    loadingService.loadInProgress();

    /* Returning the Promise so Angular's APP_INITIALIZER actually waits.
     * Previously this was fire-and-forget — bootstrap completed before
     * /user/me hydrated, the body painted in the wrong theme, and only
     * after header.component.ngOnInit ran did the theme correct itself.
     * Now: body is hidden via index.html style, and we only call
     * revealApp() once preferences (incl. theme) are loaded. */
    return authenticationService.checkAuthentication()
      .then(isAuthenticated => {
        if (!isAuthenticated) {
          // Redirect to login — body stays hidden, no flash of UI.
          authenticationService.authenticate();
          return Promise.reject('redirecting');
        }
        // BFF: tokens are HttpOnly cookies, so the Angular layer can't
        // decode the JWT to get userId/authorities. Pull them from
        // /oauth2/me before initService.init() so guards see populated
        // authorities.
        return authenticationService.loadSessionId()
          .then(() => authenticationService.loadProfile())
          .then(() => {
            // Login-flicker fix: a pure DevOps operator (no tenant-app role) belongs to the
            // ops console, not this app. Redirect to the console host NOW — authorities are
            // loaded, but BEFORE initService.init() opens the SSE stream and before the app
            // renders (body is still hidden via index.html). Previously this bounce happened
            // only in HomeRedirectGuard, i.e. AFTER the client app fully booted + rendered,
            // which is the visible "load client app → redirect → console" flicker.
            if (shouldBounceToConsole(permissionChecker)) {
              const consoleUrl = appConfig.getConsoleUrl() || `https://console.${window.location.host}/`;
              window.location.replace(consoleUrl);   // replace: no client-host history entry
              return Promise.reject('redirecting');   // short-circuit init + reveal; body stays hidden
            }
            return undefined;
          })
          .then(() => loadMyProfileForTheme(userService))
          .then(() => initService.init());
      })
      .then(() => {
        loadingService.loadEnded();
        sessionStorage.removeItem(BOOKMARK_KEY);
        if (originalHash && originalHash !== '/' && originalHash !== 'home') {
          router.navigateByUrl('/' + originalHash);
        } else {
          router.navigate(['/home']);
        }
        revealApp();
      })
      .catch(err => {
        // 'redirecting' is expected when not authenticated — keep body
        // hidden until the browser navigates away to the OAuth login page
        // (no flash of unauthorized content). Any other error: reveal so
        // the user at least sees the app (possibly degraded).
        if (err !== 'redirecting') {
          loadingService.loadEnded();
          router.navigate(['/home']);
          revealApp();
        }
      });
  };
}

/** Calls /user/me. The handler inside UserService.getMyProfile already
 *  hydrates ThemeService from user.userPreferences.theme, so by the time
 *  this Promise resolves, the data-theme attribute is final. Theme errors
 *  are swallowed — the inline-script fallback in index.html still applied
 *  the cookie/localStorage value, which is good enough to avoid a flash. */
function loadMyProfileForTheme(userService: UserService): Promise<void> {
  return new Promise<void>((resolve) => {
    userService.getMyProfile({
      onStart: () => {},
      onSuccess: (_user: User) => resolve(),
      onError: () => resolve(),
      onComplete: () => {},
    });
  });
}
